import type { ReactElement } from 'react';
import { useCallback } from 'react';
import './SearchResultList.scss';
import { Button } from '@fastnorth/react-cerberus/Button';
import { useTranslation } from 'react-i18next';
import type { AppUrlParams, SearchFragment, SearchResultFragmentOverview } from '../../../../../types';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { SearchFragmentQueryVariables, SearchFragmentResponseData } from '../queries';
import { SEARCH_FRAGMENT_QUERY } from '../queries';
import Loading from '../../../../loading/Loading';
import { useQuery } from '@apollo/client';
import GeneralError from '../../../../error/GeneralError';
import NotFound from '../../../../error/NotFound';
import ThumbnailList from '../../thumbnail/ThumbnailList';
import CarouselDialog from '../../../../carousel/CarouselDialog';
import { useEvent } from '@fastnorth/react-results-event-components';

type SearchResultProps = {
  fragmentOverview: SearchResultFragmentOverview;
};

const SearchResultList = ({ fragmentOverview }: SearchResultProps): ReactElement => {
  const { t } = useTranslation();
  const { galleryId, photoId } = useParams<AppUrlParams>() as AppUrlParams;
  const { tag, total, pageNumber: initialPageNumber } = fragmentOverview;
  const navigate = useNavigate();
  const { search } = useLocation();
  const { event } = useEvent();
  // we use a lazy query here so that we can leverage the pre-feteched results from the initial search results.
  const { data, loading, error, fetchMore } = useQuery<SearchFragmentResponseData, SearchFragmentQueryVariables>(
    SEARCH_FRAGMENT_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: { galleryId, tag, pageNumber: initialPageNumber },
    },
  );
  const { fragment } = data || {
    fragment: {
      id: '',
      pageNumber: 1,
      photos: [],
      hasMorePages: false,
      total: 0,
      type: 'tag',
      tag: '',
    } as SearchFragment,
  };

  const { pageNumber, photos, hasMorePages } = fragment;
  const showLoadMore = hasMorePages && photos.length !== fragment.total;
  const loadMorePhotos = useCallback(() => {
    return fetchMore({
      variables: { galleryId, pageNumber: pageNumber + 1, tag },
    });
  }, [fetchMore, galleryId, pageNumber, tag]);

  if (error) {
    return <GeneralError error={error} />;
  }

  if (!loading && (!photos || !photos.length)) {
    return (
      <NotFound
        message={`${t('photos_ui.search_results.results.notFound', { tag_label: tag })}`}
        cerbiconUri={'https://cdn.raceroster.com/assets/cerbicons/empty-polaroids.svg'}
      />
    );
  }

  const photoIdx = photos.findIndex(({ id }) => id === photoId);
  const photo = photos[photoIdx];
  return (
    <Loading busy={!data && loading}>
      <div className={'SearchResultList__container'}>
        <div key={tag} className={'d-flex align-items-center justify-content-between border-bottom-0 pb-3'}>
          <h2 className={'lead font-weight-semi-bold mb-0'}>
            {t('photos_ui.search_results.tag.title', { tag_label: tag })}
          </h2>
          <p className={'mb-0'}>{t('photos_ui.search_results.tag.itemsFound', { total })}</p>
        </div>
        <ThumbnailList photos={photos} relativeRoutePrefix={`${tag}`} />
        {showLoadMore && (
          <div className={'d-flex justify-content-center'}>
            <Button
              variant={'link'}
              iconLeft={'images'}
              disabled={loading}
              onClick={loadMorePhotos}
              className={`${event?.branding ? 'custom-secondary-bg-as-color' : 'text-primary'}`}>
              {loading ? t('photos_ui.search_results.tag.loading') : t('photos_ui.search_results.tag.loadMore')}
            </Button>
          </div>
        )}

        {photo && (
          <CarouselDialog
            loadingMore={loading}
            photo={photo}
            onClose={() => {
              navigate({ pathname: '../../../', search }, { relative: 'path' });
            }}
            onPrev={() => {
              const prev = photos[photoIdx - 1];
              if (prev) navigate({ pathname: `../${prev.id}`, search }, { relative: 'path' });
            }}
            onNext={() => {
              const next = photos[photoIdx + 1];
              if (!next && hasMorePages) {
                return loadMorePhotos().then(({ data }) => {
                  //we go to the first element of the new batch
                  navigate({ pathname: `../${data.fragment.photos[0].id}`, search }, { relative: 'path' });
                });
              } else {
                return next && navigate({ pathname: `../${next.id}`, search }, { relative: 'path' });
              }
            }}
          />
        )}
      </div>
    </Loading>
  );
};

export default SearchResultList;
